<template>
    <div v-if="show"
         class="fixed inset-0 z-50 overflow-y-auto"
         aria-labelledby="modal-title"
         role="dialog"
         aria-modal="true">
        <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
                @click="close"
                class="fixed inset-0 bg-stone-900/60 backdrop-blur-[2px] transition-opacity"
                aria-hidden="true">
            </div>

            <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div class="relative inline-block align-bottom bg-white dark:bg-[#0d0e13] border border-stone-200 dark:border-white/10 rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-[96vw] 2xl:max-w-[100rem] sm:w-full text-stone-900 dark:text-stone-100">
                <!-- Header -->
                <div class="flex items-center justify-between px-5 py-3 border-b border-stone-200 dark:border-white/10">
                    <div class="flex items-center gap-3">
                        <span class="flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-500/30 bg-emerald-500/10">
                            <font-awesome-icon :icon="['fas', 'gauge-high']" class="h-4 w-4 text-emerald-500" />
                        </span>
                        <div>
                            <div class="flex items-center gap-2.5">
                                <h3 class="xp-display text-base font-bold leading-6">
                                    Extrato do score
                                </h3>
                                <span class="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border"
                                      :class="trace.score_final < 50
                                          ? 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30'
                                          : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'">
                                    {{ statusLabel }}
                                </span>
                                <span v-if="trace.route === 'revisao_humana'"
                                      class="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/30">
                                    Revisão humana
                                </span>
                                <span v-if="trace.route === 'desbloqueio_revisao'"
                                      title="Catraca: bloqueado em produção, a troca de regra não desbloqueia. Liberação por revisão humana."
                                      class="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30">
                                    Desbloqueio em revisão
                                </span>
                            </div>
                            <p class="xp-mono text-[11px] text-stone-500 dark:text-stone-400 mt-0.5">
                                trace:{{ trace.id }}<template v-if="trace.seller_score_id"> · score:{{ trace.seller_score_id }}</template>
                            </p>
                        </div>
                    </div>
                    <button
                        @click="close"
                        class="text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 transition-colors"
                    >
                        <font-awesome-icon :icon="['fas', 'times']" class="h-5 w-5" />
                    </button>
                </div>

                <!-- Info strip -->
                <div class="grid grid-cols-2 lg:grid-cols-4 divide-x divide-stone-200 dark:divide-white/10 border-b border-stone-200 dark:border-white/10 bg-stone-50 dark:bg-white/2">
                    <div class="px-5 py-2">
                        <div class="xp-display text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400 dark:text-stone-500">Vendedor</div>
                        <div class="xp-mono text-xs mt-1 truncate" :title="user.email">{{ user.email }}</div>
                    </div>
                    <div class="px-5 py-2">
                        <div class="xp-display text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400 dark:text-stone-500">Origem</div>
                        <div class="text-xs mt-1">{{ sourceLabel }}</div>
                    </div>
                    <div class="px-5 py-2">
                        <div class="xp-display text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400 dark:text-stone-500">Data / Hora</div>
                        <div class="xp-mono text-xs mt-1">{{ trace.created_at }}</div>
                    </div>
                    <div class="px-5 py-2">
                        <div class="xp-display text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400 dark:text-stone-500">Movimentos</div>
                        <div class="xp-mono text-xs mt-1">
                            {{ activeSpanCount }} de {{ trace.spans.length }} regras<template v-if="trace.duration_ms !== null"> · {{ trace.duration_ms }}ms</template>
                        </div>
                    </div>
                </div>

                <!-- Por que este score (TIC2-15569, X2) -->
                <div v-if="trace.explanation" class="px-5 py-3 border-b border-stone-200 dark:border-white/10 bg-sky-500/5 dark:bg-sky-500/10">
                    <div class="flex items-start gap-3">
                        <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-sky-500/30 bg-sky-500/10 text-sky-600 dark:text-sky-400">
                            <font-awesome-icon :icon="['fas', 'circle-info']" class="h-3.5 w-3.5" />
                        </span>
                        <div class="min-w-0">
                            <div class="xp-display text-[10px] font-bold uppercase tracking-[0.18em] text-sky-600 dark:text-sky-400 mb-1">Por que este score</div>
                            <p class="text-sm leading-relaxed text-stone-800 dark:text-stone-100">{{ trace.explanation }}</p>
                        </div>
                    </div>
                </div>

                <!-- Body -->
                <div class="px-5 py-4">
                <div class="flex flex-col xl:flex-row gap-4 items-start">
                <div class="flex-1 min-w-0 w-full">

                <!-- Score panel -->
                <div class="rounded-xl border border-stone-200 dark:border-white/10 bg-stone-50/60 dark:bg-white/2 p-4 mb-4">
                    <div class="flex flex-wrap items-start gap-x-10 gap-y-4">
                        <div>
                            <div class="xp-display text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400 dark:text-stone-500 mb-1">Soma bruta</div>
                            <div class="xp-mono text-4xl font-bold tabular-nums leading-none">
                                {{ trace.score_raw }}
                            </div>
                            <div class="xp-mono text-[11px] text-stone-400 dark:text-stone-500 mt-1.5">
                                100 base {{ appliedSum >= 0 ? '+' : '' }}{{ appliedSum }} das regras
                            </div>
                        </div>

                        <div class="self-center text-stone-300 dark:text-stone-600 text-xl">→</div>

                        <div v-if="trace.ceiling !== null">
                            <div class="xp-display text-[10px] font-bold uppercase tracking-[0.18em] text-amber-600 dark:text-amber-400 mb-1">Teto</div>
                            <div class="xp-mono text-4xl font-bold tabular-nums leading-none text-amber-600 dark:text-amber-400">
                                {{ trace.ceiling }}
                            </div>
                            <div class="xp-mono text-[11px] text-stone-400 dark:text-stone-500 mt-1.5">
                                {{ ceilingRuleLabel }}
                            </div>
                        </div>

                        <div v-if="trace.ceiling !== null" class="self-center text-stone-300 dark:text-stone-600 text-xl">→</div>

                        <div>
                            <div class="xp-display text-[10px] font-bold uppercase tracking-[0.18em] mb-1"
                                 :class="trace.score_final < 50 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'">
                                Score final
                            </div>
                            <div class="xp-mono text-4xl font-bold tabular-nums leading-none"
                                 :class="trace.score_final < 50 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'">
                                {{ trace.score_final }}
                            </div>
                            <div class="xp-mono text-[11px] text-stone-400 dark:text-stone-500 mt-1.5">
                                {{ statusLabel.toLowerCase() }}{{ trace.score_final < 50 ? ' (< 50)' : '' }}
                            </div>
                        </div>

                        <div v-if="absorbedPenalty > 0" class="ml-auto max-w-xs text-[11px] text-amber-700 dark:text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 self-center">
                            A soma passou de 100: sem teto, até {{ absorbedPenalty }} pontos de penalidade seriam absorvidos pelo cap.
                        </div>
                    </div>

                    <!-- Progressão do cálculo (linha, esquerda -> direita) -->
                    <div v-if="progression" class="mt-4">
                        <div class="flex items-center justify-between mb-1">
                            <span class="xp-mono text-[10px] text-stone-400 dark:text-stone-500">progressão do cálculo</span>
                            <span class="xp-mono text-[10px] text-stone-400 dark:text-stone-500">saldo após cada movimento</span>
                        </div>
                        <svg :viewBox="`0 0 1000 ${progression.height}`" class="w-full h-auto">
                            <!-- Zona de bloqueio (< 50) -->
                            <rect x="0" :y="progression.y50" width="1000" :height="progression.height - progression.y50 - 14"
                                  class="fill-red-500/5" />
                            <line x1="0" :y1="progression.y50" x2="1000" :y2="progression.y50"
                                  class="stroke-red-400/60" stroke-width="1" stroke-dasharray="5,4" />
                            <line x1="0" :y1="progression.y100" x2="1000" :y2="progression.y100"
                                  class="stroke-stone-300 dark:stroke-white/15" stroke-width="1" />

                            <polyline
                                :points="progression.path"
                                fill="none"
                                class="xpr-line stroke-sky-500"
                                stroke-width="2"
                                stroke-linejoin="round"
                                stroke-linecap="round"
                                pathLength="1"
                                vector-effect="non-scaling-stroke"
                            />

                            <g v-for="(point, index) in progression.points" :key="point.key">
                                <circle
                                    :cx="point.x" :cy="point.y" r="3.5"
                                    :class="['xpr-dot', point.cls]"
                                    :style="{ animationDelay: (index * (900 / progression.points.length)) + 'ms' }"
                                >
                                    <title>{{ point.label }}: saldo {{ point.balance }}</title>
                                </circle>
                            </g>
                        </svg>
                        <div class="flex justify-between xp-mono text-[9px] text-stone-400 dark:text-stone-500">
                            <span>base 100</span>
                            <span>score final {{ trace.score_final }}</span>
                        </div>
                    </div>
                </div>

                <!-- Views -->
                <div class="rounded-xl border border-stone-200 dark:border-white/10 bg-white dark:bg-white/2">
                    <div class="flex items-center justify-between px-5 py-3 border-b border-stone-200 dark:border-white/10">
                        <h4 class="xp-display text-[11px] font-bold uppercase tracking-[0.18em] text-stone-400 dark:text-stone-500">
                            {{ viewTitle }}
                        </h4>
                        <div class="inline-flex rounded-lg border border-stone-200 dark:border-white/10 bg-stone-100/70 dark:bg-white/4 p-0.5">
                            <button
                                v-for="tab in viewTabs"
                                :key="tab.id"
                                type="button"
                                class="px-3 py-1 text-xs font-medium rounded-md transition-colors"
                                :class="view === tab.id
                                    ? 'bg-white dark:bg-[#1c1f2b] text-stone-900 dark:text-white shadow-sm border border-stone-200 dark:border-white/10'
                                    : 'text-stone-500 dark:text-stone-400'"
                                @click="view = tab.id"
                            >
                                {{ tab.label }}
                            </button>
                        </div>
                    </div>

                    <!-- ═══ Extrato (waterfall cumulativo) ═══ -->
                    <div v-if="view === 'extrato'" class="overflow-x-auto">
                        <div class="min-w-[720px]">
                            <div class="flex border-b border-stone-200 dark:border-white/10 bg-stone-50 dark:bg-white/3">
                                <div class="w-64 shrink-0 px-5 py-2 border-r border-stone-200 dark:border-white/10">
                                    <span class="xp-display text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400 dark:text-stone-500">Movimento</span>
                                </div>
                                <div class="flex-1 relative h-7">
                                    <span
                                        v-for="tick in axisTicks"
                                        :key="tick"
                                        class="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 xp-mono text-[9px] text-stone-400 dark:text-stone-500"
                                        :style="{ left: pct(tick) + '%' }"
                                    >{{ tick }}</span>
                                </div>
                                <div class="w-20 shrink-0 px-3 py-2 border-l border-stone-200 dark:border-white/10 text-right">
                                    <span class="xp-display text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400 dark:text-stone-500">Saldo</span>
                                </div>
                            </div>

                            <div class="relative">
                                <!-- Zona de bloqueio + linha 50, atravessando o extrato inteiro -->
                                <div class="absolute inset-y-0 pointer-events-none"
                                     :style="{ left: chartLeft, right: chartRight }">
                                    <div class="absolute inset-y-0 left-0 bg-red-500/4 dark:bg-red-500/6"
                                         :style="{ width: pct(50) + '%' }"></div>
                                    <div class="absolute inset-y-0 border-l border-dashed border-red-400/60"
                                         :style="{ left: pct(50) + '%' }"></div>
                                    <div class="absolute inset-y-0 border-l border-stone-300/60 dark:border-white/15"
                                         :style="{ left: pct(100) + '%' }"></div>
                                </div>

                                <div class="divide-y divide-stone-100 dark:divide-white/5">
                                    <button
                                        v-for="(row, index) in ledgerRows"
                                        :key="row.key"
                                        type="button"
                                        class="xpr-row w-full flex items-stretch text-left transition-colors"
                                        :class="[
                                            row.span && selectedRule === row.span.rule_name
                                                ? 'bg-emerald-500/6 dark:bg-white/5'
                                                : 'hover:bg-stone-50 dark:hover:bg-white/3',
                                            row.muted ? 'opacity-50' : ''
                                        ]"
                                        :style="{ animationDelay: (index * 35) + 'ms' }"
                                        @click="row.span && (selectedRule = row.span.rule_name)"
                                    >
                                        <div class="w-64 shrink-0 flex items-center gap-2 px-5 py-2 border-r border-stone-100 dark:border-white/5 overflow-hidden">
                                            <span class="w-2 h-2 rounded-full shrink-0" :class="row.dot"></span>
                                            <span class="xp-mono text-[11px] truncate" :class="row.emphasis ? 'font-bold' : ''">
                                                {{ row.label }}
                                            </span>
                                            <span v-if="row.badge"
                                                  class="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-px rounded border shrink-0"
                                                  :class="row.badgeClass">
                                                {{ row.badge }}
                                            </span>
                                        </div>

                                        <div class="flex-1 relative h-9">
                                            <template v-if="row.width > 0">
                                                <div class="xpr-bar absolute top-1/2 -translate-y-1/2 h-3.5 rounded-[3px]"
                                                     :class="row.bar"
                                                     :style="{ left: row.left + '%', width: row.width + '%', animationDelay: (index * 35 + 120) + 'ms' }"></div>
                                            </template>
                                            <template v-else>
                                                <span class="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full"
                                                      :class="row.dot"
                                                      :style="{ left: row.left + '%' }"></span>
                                            </template>
                                            <span v-if="row.delta !== null"
                                                  class="absolute top-1/2 -translate-y-1/2 xp-mono text-[10px] font-semibold whitespace-nowrap px-1.5"
                                                  :class="row.deltaClass"
                                                  :style="deltaLabelStyle(row)">
                                                {{ row.delta > 0 ? '+' + row.delta : row.delta }}
                                            </span>
                                        </div>

                                        <div class="w-20 shrink-0 flex items-center justify-end px-3 border-l border-stone-100 dark:border-white/5">
                                            <span class="xp-mono text-[11px] tabular-nums"
                                                  :class="row.emphasis
                                                      ? (row.balance < 50 ? 'font-bold text-red-600 dark:text-red-400' : 'font-bold text-emerald-600 dark:text-emerald-400')
                                                      : 'text-stone-500 dark:text-stone-400'">
                                                {{ row.balance }}
                                            </span>
                                        </div>
                                    </button>
                                </div>
                            </div>

                            <!-- Legenda -->
                            <div class="flex flex-wrap items-center gap-x-5 gap-y-1 px-5 py-2.5 border-t border-stone-200 dark:border-white/10 bg-stone-50 dark:bg-white/2">
                                <span class="flex items-center gap-1.5 xp-mono text-[10px] text-stone-400 dark:text-stone-500">
                                    <span class="w-1.5 h-1.5 rounded-full bg-red-500"></span> débito
                                </span>
                                <span class="flex items-center gap-1.5 xp-mono text-[10px] text-stone-400 dark:text-stone-500">
                                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> crédito
                                </span>
                                <span class="flex items-center gap-1.5 xp-mono text-[10px] text-stone-400 dark:text-stone-500">
                                    <span class="w-1.5 h-1.5 rounded-full bg-amber-500"></span> teto / clamp
                                </span>
                                <span class="ml-auto xp-mono text-[10px] text-red-400/80">← zona de bloqueio termina no 50</span>
                            </div>
                        </div>
                    </div>

                    <!-- ═══ Regras (lista) ═══ -->
                    <div v-else-if="view === 'regras'">
                        <div class="divide-y divide-stone-100 dark:divide-white/5">
                            <button
                                v-for="span in orderedSpans"
                                :key="span.rule_name"
                                type="button"
                                class="w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors border-l-2"
                                :class="[
                                    selectedRule === span.rule_name
                                        ? 'bg-emerald-500/6 dark:bg-white/5 border-emerald-500'
                                        : 'hover:bg-stone-50 dark:hover:bg-white/3 border-transparent',
                                    span.weight === 0 && !span.guard ? 'opacity-55' : ''
                                ]"
                                @click="selectedRule = span.rule_name"
                            >
                                <span class="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border text-[11px]"
                                      :class="spanIconClass(span)">
                                    {{ spanGlyph(span) }}
                                </span>
                                <div class="min-w-0 flex-1">
                                    <div class="flex items-center gap-2 flex-wrap">
                                        <span class="xp-mono text-xs font-medium truncate">{{ formatRuleName(span.rule_name) }}</span>
                                        <span v-if="span.guard"
                                              class="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-px rounded border border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400">
                                            {{ span.guard }}
                                        </span>
                                        <span v-for="flag in activeFlags(span)" :key="flag"
                                              class="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-px rounded border border-orange-500/30 bg-orange-500/10 text-orange-600 dark:text-orange-400">
                                            {{ flag }}
                                        </span>
                                        <span v-if="span.ceiling !== null"
                                              class="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-px rounded border border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400">
                                            teto {{ span.ceiling }}
                                        </span>
                                    </div>
                                    <div class="text-[11px] mt-0.5 truncate" :class="resultTextClass(span)">
                                        {{ spanResult(span) }}
                                    </div>
                                </div>

                                <div class="w-32 shrink-0 flex items-center justify-end gap-2">
                                    <span class="relative h-1 w-14 rounded-full bg-stone-200 dark:bg-white/10 overflow-hidden">
                                        <span class="absolute inset-y-0 left-0 rounded-full"
                                              :class="span.weight < 0 ? 'bg-red-500' : (span.weight > 0 ? 'bg-emerald-500' : 'bg-stone-300 dark:bg-stone-600')"
                                              :style="{ width: Math.min(100, Math.abs(span.weight) / 50 * 100) + '%' }"></span>
                                    </span>
                                    <span class="xp-mono text-[11px] tabular-nums w-8 text-right"
                                          :class="span.weight < 0 ? 'text-red-600 dark:text-red-400 font-semibold' : (span.weight > 0 ? 'text-emerald-600 dark:text-emerald-400 font-semibold' : 'text-stone-400 dark:text-stone-500')">
                                        {{ span.weight > 0 ? '+' + span.weight : span.weight }}
                                    </span>
                                </div>
                            </button>
                        </div>
                    </div>

                    <!-- ═══ Comparar com outro cálculo (TIC2-15569, X5) ═══ -->
                    <div v-else-if="view === 'comparar'" class="px-5 py-4">
                        <div v-if="diffLoading" class="text-xs text-stone-400 dark:text-stone-500">Carregando comparação...</div>
                        <div v-else-if="diffError" class="text-xs text-red-600 dark:text-red-400">{{ diffError }}</div>
                        <template v-else-if="diff">
                            <div class="flex flex-wrap items-center gap-3 mb-3">
                                <span class="xp-mono text-[11px] text-stone-500 dark:text-stone-400">Comparar este cálculo com</span>
                                <select
                                    v-model="diffFrom"
                                    @change="loadDiff"
                                    class="text-xs rounded-lg border border-stone-200 dark:border-white/10 bg-white dark:bg-[#1c1f2b] text-stone-800 dark:text-stone-100 px-2 py-1"
                                >
                                    <option v-for="candidate in diff.candidates" :key="candidate.id" :value="candidate.id">
                                        {{ candidate.created_at }} · score {{ candidate.score_final }} · {{ candidate.source }}
                                    </option>
                                </select>
                            </div>

                            <div v-if="!diff.from" class="text-xs text-stone-500 dark:text-stone-400">{{ diff.summary.message }}</div>

                            <template v-else>
                                <div class="rounded-lg border px-3 py-2 text-sm leading-relaxed mb-3"
                                     :class="diff.summary.crossed_block_line
                                         ? 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300'
                                         : 'border-sky-500/30 bg-sky-500/10 text-stone-800 dark:text-stone-100'">
                                    {{ diff.summary.explanation }}
                                </div>

                                <div class="grid grid-cols-3 gap-3 mb-3 text-center">
                                    <div>
                                        <div class="xp-display text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400 dark:text-stone-500">Antes</div>
                                        <div class="xp-mono text-2xl font-bold tabular-nums" :class="diff.from.score_final < 50 ? 'text-red-600 dark:text-red-400' : ''">{{ diff.from.score_final }}</div>
                                        <div class="xp-mono text-[10px] text-stone-400 dark:text-stone-500">{{ diff.from.created_at }}</div>
                                    </div>
                                    <div>
                                        <div class="xp-display text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400 dark:text-stone-500">Diferença</div>
                                        <div class="xp-mono text-2xl font-bold tabular-nums"
                                             :class="diff.summary.score_delta < 0 ? 'text-red-600 dark:text-red-400' : (diff.summary.score_delta > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-stone-400')">
                                            {{ diff.summary.score_delta > 0 ? '+' : '' }}{{ diff.summary.score_delta }}
                                        </div>
                                    </div>
                                    <div>
                                        <div class="xp-display text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400 dark:text-stone-500">Depois</div>
                                        <div class="xp-mono text-2xl font-bold tabular-nums" :class="diff.to.score_final < 50 ? 'text-red-600 dark:text-red-400' : ''">{{ diff.to.score_final }}</div>
                                        <div class="xp-mono text-[10px] text-stone-400 dark:text-stone-500">{{ diff.to.created_at }}</div>
                                    </div>
                                </div>

                                <div class="overflow-x-auto">
                                    <table class="w-full text-xs">
                                        <thead>
                                            <tr class="text-left xp-display text-[10px] uppercase tracking-[0.18em] text-stone-400 dark:text-stone-500">
                                                <th class="py-1.5 pr-3 font-bold">Regra</th>
                                                <th class="py-1.5 pr-3 font-bold text-right">Antes</th>
                                                <th class="py-1.5 pr-3 font-bold text-right">Depois</th>
                                                <th class="py-1.5 pr-3 font-bold text-right">Δ</th>
                                                <th class="py-1.5 font-bold">Entradas que mudaram</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="row in diff.rows" :key="row.rule_name"
                                                class="border-t border-stone-100 dark:border-white/5"
                                                :class="row.delta !== 0 ? 'font-semibold' : 'text-stone-400 dark:text-stone-500'">
                                                <td class="py-1.5 pr-3">
                                                    {{ formatRuleName(row.rule_name) }}
                                                    <span v-if="row.status === 'nova' || row.status === 'removida'"
                                                          class="ml-1 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-px rounded border border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400">
                                                        {{ row.status }}
                                                    </span>
                                                    <span v-if="row.before_version && row.after_version && row.before_version !== row.after_version"
                                                          class="ml-1 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-px rounded border border-purple-500/30 bg-purple-500/10 text-purple-600 dark:text-purple-400">
                                                        v{{ row.before_version }} → v{{ row.after_version }}
                                                    </span>
                                                </td>
                                                <td class="py-1.5 pr-3 text-right xp-mono tabular-nums">{{ row.before === null ? '·' : row.before }}</td>
                                                <td class="py-1.5 pr-3 text-right xp-mono tabular-nums">{{ row.after === null ? '·' : row.after }}</td>
                                                <td class="py-1.5 pr-3 text-right xp-mono tabular-nums"
                                                    :class="row.delta < 0 ? 'text-red-600 dark:text-red-400' : (row.delta > 0 ? 'text-emerald-600 dark:text-emerald-400' : '')">
                                                    {{ row.delta > 0 ? '+' + row.delta : row.delta }}
                                                </td>
                                                <td class="py-1.5">
                                                    <span v-for="change in row.inputs_changed" :key="change.key"
                                                          class="inline-block mr-1 mb-0.5 xp-mono text-[10px] px-1.5 py-px rounded border border-stone-200 dark:border-white/10 text-stone-600 dark:text-stone-300">
                                                        {{ change.key }}: {{ formatValue(change.before) }} → {{ formatValue(change.after) }}
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </template>
                        </template>
                    </div>
                </div>

                </div>

                <!-- Sidebar -->
                <aside class="w-full xl:w-80 2xl:w-96 shrink-0 space-y-4">
                    <div v-if="selectedSpan" class="rounded-xl border border-stone-200 dark:border-white/10 bg-stone-50 dark:bg-white/2 p-4">
                        <div class="flex items-center gap-2.5 mb-3">
                            <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border text-sm"
                                  :class="spanIconClass(selectedSpan)">
                                {{ spanGlyph(selectedSpan) }}
                            </span>
                            <div class="min-w-0 flex-1">
                                <div class="xp-mono text-sm font-bold truncate">{{ formatRuleName(selectedSpan.rule_name) }}</div>
                                <div class="xp-mono text-[10px] text-stone-400 dark:text-stone-500">{{ selectedSpan.rule_name }}</div>
                            </div>
                            <span class="xp-mono text-xs font-bold tabular-nums px-2 py-1 rounded border shrink-0"
                                  :class="selectedSpan.weight < 0
                                      ? 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30'
                                      : (selectedSpan.weight > 0
                                          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                                          : 'bg-stone-500/10 text-stone-500 dark:text-stone-400 border-stone-500/30')">
                                {{ selectedSpan.weight > 0 ? '+' + selectedSpan.weight : selectedSpan.weight }}
                            </span>
                        </div>

                        <div class="rounded-lg border px-3 py-2 text-xs mb-3" :class="resultBoxClass(selectedSpan)">
                            {{ spanResult(selectedSpan) }}
                        </div>

                        <div v-if="spanEntries(selectedSpan).length > 0"
                             class="rounded-lg border border-stone-200 dark:border-white/10 bg-white dark:bg-white/2 divide-y divide-stone-100 dark:divide-white/5">
                            <div
                                v-for="entry in spanEntries(selectedSpan)"
                                :key="entry.key"
                                class="flex items-center justify-between gap-3 px-3 py-1.5"
                            >
                                <span class="xp-mono text-[11px] text-stone-500 dark:text-stone-400">{{ entry.key }}</span>
                                <span class="xp-mono text-[11px] text-right break-all">{{ entry.value }}</span>
                            </div>
                        </div>
                        <p v-else class="text-[11px] text-stone-400 dark:text-stone-500 italic">
                            Este run foi gravado antes da instrumentação desta regra; os próximos trazem os inputs.
                        </p>

                        <!-- Passo a passo da regra (TIC2-15569, X1) -->
                        <div v-if="selectedSpan.steps && selectedSpan.steps.length" class="mt-3">
                            <div class="xp-display text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400 dark:text-stone-500 mb-2">
                                Passo a passo da regra<template v-if="selectedSpan.rule_version"> · v{{ selectedSpan.rule_version }}</template>
                            </div>
                            <ol class="space-y-1.5">
                                <li v-for="(step, index) in selectedSpan.steps" :key="index"
                                    class="rounded-lg border border-stone-200 dark:border-white/10 bg-white dark:bg-white/2 px-3 py-2">
                                    <div class="xp-mono text-[11px] font-semibold">{{ index + 1 }}. {{ step.etapa }}</div>
                                    <div class="text-[11px] mt-0.5" :class="stepClass(step)">{{ step.resultado }}</div>
                                    <div v-if="step.usou && Object.keys(step.usou).length" class="mt-1 flex flex-wrap gap-1">
                                        <span v-for="(value, key) in step.usou" :key="key"
                                              class="xp-mono text-[10px] px-1.5 py-px rounded border border-stone-200 dark:border-white/10 text-stone-500 dark:text-stone-400">
                                            {{ key }}: {{ formatValue(value) }}
                                        </span>
                                    </div>
                                </li>
                            </ol>
                        </div>
                    </div>
                </aside>
                </div>
                </div>

                <!-- Footer -->
                <div class="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-stone-200 dark:border-white/10 bg-stone-50 dark:bg-white/2">
                    <p class="text-xs text-stone-400 dark:text-stone-500">
                        {{ footerNote }}
                    </p>
                    <div class="flex items-center gap-2">
                        <button
                            @click="exportJson"
                            class="border border-stone-200 dark:border-white/10 text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-white/5 text-sm font-semibold rounded-lg px-4 py-2 transition-colors"
                        >
                            Exportar JSON
                        </button>
                        <button
                            @click="close"
                            class="bg-stone-900 dark:bg-white text-white dark:text-stone-900 hover:bg-stone-700 dark:hover:bg-stone-200 text-sm font-semibold rounded-lg px-5 py-2 transition-colors"
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue'
import axios from 'axios'
import { scoreRuleLabels } from './scoreRuleLabels'

const CHART_LEFT = '16rem'
const CHART_RIGHT = '5rem'

export default defineComponent({
    name: 'ScoreTraceModal',
    props: {
        show: { type: Boolean, default: false },
        trace: { type: Object, default: () => ({}) },
        user: { type: Object, required: true },
        initialRule: { type: String, default: null },
        // Run experimental (shadow): ajusta labels e deixa claro que nada foi persistido
        experimental: { type: Boolean, default: false },
    },
    emits: ['close'],
    data() {
        return {
            selectedRule: null,
            view: 'extrato',
            chartLeft: CHART_LEFT,
            chartRight: CHART_RIGHT,
            diff: null,
            diffLoading: false,
            diffError: null,
            diffFrom: null,
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.view = 'extrato'
                this.selectedRule = this.initialRule || this.orderedSpans[0]?.rule_name || null
            }
        },
        view(newVal) {
            if (newVal === 'comparar' && !this.diff && !this.diffLoading) {
                this.loadDiff()
            }
        },
        'trace.id'() {
            this.diff = null
            this.diffFrom = null
            this.diffError = null
        },
    },
    computed: {
        viewTabs() {
            const tabs = [
                { id: 'extrato', label: 'Extrato' },
                { id: 'regras', label: 'Regras' },
            ]
            if (!this.experimental && this.trace?.id) {
                tabs.push({ id: 'comparar', label: 'Comparar' })
            }
            return tabs
        },
        viewTitle() {
            if (this.view === 'comparar') return 'Comparar com outro cálculo'
            return this.view === 'extrato' ? 'Extrato do cálculo' : 'Regras do run'
        },
        orderedSpans() {
            if (!this.trace?.spans) return []
            return [...this.trace.spans].sort((a, b) => a.weight - b.weight)
        },
        selectedSpan() {
            return this.orderedSpans.find((span) => span.rule_name === this.selectedRule) || null
        },
        activeSpanCount() {
            return this.orderedSpans.filter((span) => span.weight !== 0).length
        },
        appliedSum() {
            return this.orderedSpans.reduce((sum, span) => sum + span.weight, 0)
        },
        absorbedPenalty() {
            if (!this.trace || this.trace.score_raw <= 100) return 0
            const negative = this.orderedSpans
                .filter((span) => span.weight < 0)
                .reduce((sum, span) => sum + span.weight, 0)
            return Math.min(Math.abs(negative), this.trace.score_raw - 100)
        },
        ceilingRuleLabel() {
            const rule = this.orderedSpans.find((span) => span.ceiling === this.trace.ceiling)
            return rule ? 'imposto por ' + this.formatRuleName(rule.rule_name) : 'teto aplicado'
        },
        sourceLabel() {
            const labels = {
                batch: 'Batch noturno (22h)',
                intraday: 'Recálculo intraday',
                experimental: 'Execução experimental',
            }
            return labels[this.trace?.source] || this.trace?.source
        },
        statusLabel() {
            const blocked = this.trace.score_final < 50
            if (this.experimental) return blocked ? 'Seria bloqueado' : 'Seria liberado'
            return blocked ? 'Bloqueado' : 'Liberado'
        },
        footerNote() {
            return this.experimental
                ? 'Execução experimental: nenhuma alteração foi persistida no score do vendedor.'
                : 'Cálculo de produção: este é o score real gravado para o vendedor.'
        },
        // Domínio do gráfico: cobre todos os saldos intermediários, o 100 e o piso 15
        chartDomain() {
            let balance = 100
            let min = 15
            let max = 100

            const spans = this.trace?.spans || []

            spans.forEach((span) => {
                balance += span.weight
                min = Math.min(min, balance)
                max = Math.max(max, balance)
            })

            const pad = Math.max(4, Math.round((max - min) * 0.04))

            return { min: min - pad, max: max + pad }
        },
        axisTicks() {
            const ticks = [15, 50, 100]
            const { max } = this.chartDomain
            if (max > 112) ticks.push(Math.round(max - 4))
            return ticks
        },
        /**
         * O extrato: 100 de base, um movimento por regra (ordem de execução),
         * depois as operações estruturais (clamp, teto) e o saldo final.
         */
        /**
         * Pontos do chart de linha: saldo após cada movimento do extrato,
         * distribuídos da esquerda (base) para a direita (score final).
         */
        progression() {
            const rows = this.ledgerRows
            if (rows.length < 2) return null

            const HEIGHT = 150
            const TOP = 12
            const BOTTOM = 14
            const { min, max } = this.chartDomain

            const yFor = (value) => {
                if (max === min) return HEIGHT - BOTTOM
                const ratio = (value - min) / (max - min)
                return TOP + (1 - ratio) * (HEIGHT - TOP - BOTTOM)
            }

            const points = rows.map((row, index) => ({
                key: row.key,
                x: rows.length === 1 ? 0 : 14 + (index / (rows.length - 1)) * 972,
                y: yFor(row.balance),
                balance: row.balance,
                label: row.label,
                cls: row.key === 'final'
                    ? (row.balance < 50 ? 'fill-red-500' : 'fill-emerald-500')
                    : row.dot.replace(/bg-/g, 'fill-').split(' ')[0],
            }))

            return {
                height: HEIGHT,
                y50: yFor(50),
                y100: yFor(100),
                points,
                path: points.map((point) => `${point.x},${point.y}`).join(' '),
            }
        },
        ledgerRows() {
            if (!this.trace?.spans) return []

            const rows = []
            let balance = 100

            rows.push({
                key: 'base',
                label: 'Base do score',
                delta: null,
                balance,
                left: this.pct(balance),
                width: 0,
                dot: 'bg-stone-400 dark:bg-stone-500',
                bar: '',
                emphasis: false,
                muted: false,
                span: null,
                badge: null,
            })

            this.trace.spans.forEach((span) => {
                const prev = balance
                balance += span.weight
                const credit = span.weight > 0

                rows.push({
                    key: span.rule_name,
                    label: this.formatRuleName(span.rule_name),
                    delta: span.weight !== 0 ? span.weight : null,
                    deltaClass: credit ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400',
                    balance,
                    left: this.pct(Math.min(prev, balance)),
                    width: Math.abs(this.pct(balance) - this.pct(prev)),
                    dot: span.weight === 0
                        ? 'bg-stone-300 dark:bg-stone-600'
                        : (credit ? 'bg-emerald-500' : 'bg-red-500'),
                    bar: credit ? 'bg-emerald-500/85' : 'bg-red-500/85',
                    emphasis: false,
                    muted: span.weight === 0 && !span.guard,
                    span,
                    badge: span.guard || (span.ceiling !== null ? `teto ${span.ceiling}` : null),
                    badgeClass: 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400',
                })
            })

            const raw = balance
            let current = raw

            if (raw > 100 || raw < 15) {
                const clamped = Math.min(100, Math.max(15, raw))
                rows.push({
                    key: 'clamp',
                    label: raw > 100 ? 'Clamp no cap 100' : 'Clamp no piso 15',
                    delta: clamped - raw,
                    deltaClass: 'text-amber-600 dark:text-amber-400',
                    balance: clamped,
                    left: this.pct(Math.min(raw, clamped)),
                    width: Math.abs(this.pct(clamped) - this.pct(raw)),
                    dot: 'bg-amber-500',
                    bar: 'xpr-hatch',
                    emphasis: false,
                    muted: false,
                    span: null,
                    badge: null,
                })
                current = clamped
            }

            if (this.trace.ceiling !== null && this.trace.ceiling < current) {
                rows.push({
                    key: 'teto',
                    label: this.ceilingRuleLabel.replace('imposto por ', 'Teto: '),
                    delta: this.trace.ceiling - current,
                    deltaClass: 'text-amber-600 dark:text-amber-400',
                    balance: this.trace.ceiling,
                    left: this.pct(this.trace.ceiling),
                    width: Math.abs(this.pct(current) - this.pct(this.trace.ceiling)),
                    dot: 'bg-amber-500',
                    bar: 'bg-amber-500/85',
                    emphasis: false,
                    muted: false,
                    span: null,
                    badge: null,
                })
                current = this.trace.ceiling
            }

            rows.push({
                key: 'final',
                label: 'Score final',
                delta: null,
                balance: this.trace.score_final,
                left: this.pct(this.trace.score_final),
                width: 0,
                dot: this.trace.score_final < 50 ? 'bg-red-500' : 'bg-emerald-500',
                bar: '',
                emphasis: true,
                muted: false,
                span: null,
                badge: null,
            })

            return rows
        },
    },
    methods: {
        stepClass(step) {
            const result = String(step?.resultado || '')
            if (result.startsWith('BARROU') || result.startsWith('aplicado') || result.startsWith('teto') || result.startsWith('penalidade -')) {
                return 'text-amber-700 dark:text-amber-400'
            }
            return 'text-stone-500 dark:text-stone-400'
        },
        loadDiff() {
            if (!this.trace?.id || !this.user?.id) return
            this.diffLoading = true
            this.diffError = null
            const params = { to: this.trace.id }
            if (this.diffFrom) params.from = this.diffFrom
            axios
                .get(`/api/user/${this.user.id}/score/explain`, { params })
                .then(({ data }) => {
                    this.diff = data
                    this.diffFrom = data.from ? data.from.id : null
                })
                .catch(() => {
                    this.diffError = 'Não foi possível carregar a comparação deste cálculo.'
                })
                .finally(() => {
                    this.diffLoading = false
                })
        },
        pct(value) {
            const { min, max } = this.chartDomain
            if (max === min) return 0
            return Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100))
        },
        deltaLabelStyle(row) {
            const end = row.left + row.width
            if (end <= 82) return { left: end + '%' }
            return { right: (100 - row.left) + '%' }
        },
        spanGlyph(span) {
            if (span.rule_name.startsWith('chargeback_rate')) return '◆'
            if (span.weight < 0) return '↘'
            if (span.weight > 0) return '↗'
            if (span.guard) return '✓'
            return '·'
        },
        spanIconClass(span) {
            if (span.weight < 0) return 'border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400'
            if (span.weight > 0) return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
            if (span.guard) return 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400'
            return 'border-stone-200 dark:border-white/10 text-stone-400 dark:text-stone-500'
        },
        spanResult(span) {
            if (span.guard) return `guard ${span.guard}: penalidade contida neste run`
            if (span.weight < 0) return `penalizou ${span.weight} pontos`
            if (span.weight > 0) return `bonificou +${span.weight} pontos`
            return 'não pontuou neste run'
        },
        resultTextClass(span) {
            if (span.weight < 0) return 'text-red-600 dark:text-red-400'
            if (span.weight > 0) return 'text-emerald-600 dark:text-emerald-400'
            if (span.guard) return 'text-amber-600 dark:text-amber-400'
            return 'text-stone-400 dark:text-stone-500'
        },
        resultBoxClass(span) {
            if (span.weight < 0) return 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-400'
            if (span.weight > 0) return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
            if (span.guard) return 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400'
            return 'border-stone-200 dark:border-white/10 text-stone-500 dark:text-stone-400'
        },
        activeFlags(span) {
            if (!span.flags) return []
            return Object.entries(span.flags)
                .filter(([, value]) => value === true)
                .map(([key]) => key.replace(/_/g, ' '))
        },
        spanEntries(span) {
            const entries = []
            if (span.inputs) {
                Object.entries(span.inputs).forEach(([key, value]) => {
                    entries.push({ key, value: this.formatValue(value) })
                })
            }
            if (span.value !== null && span.value !== undefined) {
                entries.unshift({ key: 'valor da regra', value: this.formatValue(span.value) })
            }
            return entries
        },
        formatValue(value) {
            if (value === null || value === undefined) return '-'
            if (typeof value === 'boolean') return value ? 'sim' : 'não'
            if (typeof value === 'object') return JSON.stringify(value)
            return value
        },
        formatRuleName(ruleName) {
            return scoreRuleLabels[ruleName] || ruleName.replace(/_/g, ' ')
        },
        exportJson() {
            const blob = new Blob([JSON.stringify(this.trace, null, 2)], { type: 'application/json' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `score-trace-${this.trace.id || 'run'}.json`
            link.click()
            URL.revokeObjectURL(url)
        },
        close() {
            this.$emit('close')
        },
    },
})
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600&display=swap');

.xp-display {
    font-family: 'Archivo', 'Segoe UI', sans-serif;
}

.xp-mono {
    font-family: 'IBM Plex Mono', 'SFMono-Regular', Menlo, monospace;
}
</style>

<style scoped>
.xpr-row {
    animation: xpr-row-in 0.32s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.xpr-bar {
    transform-origin: left center;
    animation: xpr-bar-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.xpr-hatch {
    background-image: repeating-linear-gradient(
        -45deg,
        rgba(245, 158, 11, 0.75) 0,
        rgba(245, 158, 11, 0.75) 3px,
        rgba(245, 158, 11, 0.25) 3px,
        rgba(245, 158, 11, 0.25) 6px
    );
}

@keyframes xpr-row-in {
    from {
        opacity: 0;
        transform: translateX(-10px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

.xpr-line {
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
    animation: xpr-line-draw 0.9s cubic-bezier(0.4, 0, 0.2, 1) 0.15s forwards;
}

.xpr-dot {
    opacity: 0;
    animation: xpr-dot-in 0.25s ease-out forwards;
}

@keyframes xpr-line-draw {
    to {
        stroke-dashoffset: 0;
    }
}

@keyframes xpr-dot-in {
    to {
        opacity: 1;
    }
}

@keyframes xpr-bar-in {
    from {
        transform: translateY(-50%) scaleX(0);
    }
    to {
        transform: translateY(-50%) scaleX(1);
    }
}

@media (prefers-reduced-motion: reduce) {
    .xpr-row,
    .xpr-bar,
    .xpr-dot,
    .xpr-line {
        animation: none;
        opacity: 1;
        stroke-dashoffset: 0;
    }
}
</style>
